import { useMemo } from "react";
import en from "@/i18n/step-up-locales/en.json";
import ja from "@/i18n/step-up-locales/ja.json";
import ko from "@/i18n/step-up-locales/ko.json";
import ar from "@/i18n/step-up-locales/ar.json";
import ptBr from "@/i18n/step-up-locales/pt-br.json";
import es from "@/i18n/step-up-locales/es.json";
import de from "@/i18n/step-up-locales/de.json";
import fr from "@/i18n/step-up-locales/fr.json";
import zhHk from "@/i18n/step-up-locales/zh-hk.json";
import hi from "@/i18n/step-up-locales/hi.json";

export interface StepUpLocaleCatalog {
  locale: string;
  dir: "ltr" | "rtl";
  ui: Record<string, string>;
}

/**
 * Locale catalog for the step-up finishes that happen after a re-authentication
 * round trip (ALL-5021): the in-flight label, the failure copy and the expired
 * record notice shown by pages that resume a suspended write.
 *
 * Same 10-locale set, same flat `ui` key map and the same dependency-free
 * key -> catalog approach as src/i18n/use-hot-wallets-locale.ts (this app has no
 * i18n library), and its own catalog rather than growing the error-page one:
 * src/i18n/error-locales is a synced copy of a canonical design-system artifact
 * verified byte-for-byte by scripts/verify-error-page-i18n.mjs, so feature copy
 * does not belong in it.
 */
export const STEP_UP_LOCALES: Record<string, StepUpLocaleCatalog> = {
  en: en as StepUpLocaleCatalog,
  ja: ja as StepUpLocaleCatalog,
  ko: ko as StepUpLocaleCatalog,
  ar: ar as StepUpLocaleCatalog,
  "pt-br": ptBr as StepUpLocaleCatalog,
  es: es as StepUpLocaleCatalog,
  de: de as StepUpLocaleCatalog,
  fr: fr as StepUpLocaleCatalog,
  "zh-hk": zhHk as StepUpLocaleCatalog,
  hi: hi as StepUpLocaleCatalog,
};

export const DEFAULT_STEP_UP_LOCALE = "en";

/** Same resolution order as resolveErrorLocale/resolveMobileSigningLocale:
 * explicit `?locale=` query param, then the browser's language, then English. */
export function resolveStepUpLocale(searchParams?: URLSearchParams): string {
  const requested = searchParams?.get("locale");
  if (requested && STEP_UP_LOCALES[requested]) return requested;

  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase();
    if (nav && STEP_UP_LOCALES[nav]) return nav;
    const short = nav?.split("-")[0];
    if (short && STEP_UP_LOCALES[short]) return short;
  }
  return DEFAULT_STEP_UP_LOCALE;
}

export function useStepUpLocale(searchParams?: URLSearchParams): StepUpLocaleCatalog {
  const search = searchParams?.toString() ?? "";
  return useMemo(() => {
    const code = resolveStepUpLocale(searchParams);
    return STEP_UP_LOCALES[code] ?? STEP_UP_LOCALES[DEFAULT_STEP_UP_LOCALE];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
}
